import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { Sparkles, ChevronDown, User, School, Users, GraduationCap } from "lucide-react";

// Only ever visible to the ~6 seeded sales-showcase accounts (user.demo_group_id
// is null for every real account) — lets one login flip between the four
// personas the product has, backed by real demo data (Muna Matric, a demo
// principal, teacher, and school-enrolled student), without juggling four
// separate sets of credentials.
//
// Rendered as a normal-flow strip ABOVE the site header (not position:fixed)
// — a fixed top-left overlay collided with the header's own logo, which
// also lives in the top-left corner (confirmed live: the pill sat directly
// on top of the HikmahVerses wordmark). Sitting in-flow above <Nav/> instead
// pushes the header down a few pixels and never overlaps anything.
const VIEWS = [
  { target: "normal", label: "Normal member", icon: User, home: "/dashboard" },
  { target: "principal", label: "Principal", icon: School, home: "/principal" },
  { target: "teacher", label: "Teacher", icon: Users, home: "/teacher" },
  { target: "student", label: "School student", icon: GraduationCap, home: "/dashboard" },
];

export default function DemoViewSwitcher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  if (!user || !user.demo_group_id) return null;

  const current = VIEWS.find((v) => v.target === user.demo_slot) || VIEWS[0];

  const switchTo = async (target) => {
    if (switching || target === user.demo_slot) { setOpen(false); return; }
    setSwitching(true);
    try {
      const { data } = await client.post("/demo/switch-view", { target });
      if (data.access_token) localStorage.setItem("hv_token", data.access_token);
      // A hard navigation, not react-router's navigate() — switching identity
      // while deep in a role-gated nested route (e.g. /principal) raced
      // against that route's own guard re-evaluating with a stale user on
      // the same tick, which could bounce through /today with `replace`
      // before the new role ever settled (confirmed live: principal ->
      // teacher got stuck on /today instead of reaching /teacher). A full
      // reload guarantees the old gated page is torn down — and every
      // per-persona component's local state reset — before anything renders
      // with the new identity.
      window.location.href = VIEWS.find((v) => v.target === target)?.home || "/dashboard";
    } catch {
      // Best-effort — if it fails, the account just stays on its current view.
      setSwitching(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative bg-hv-navy text-white" data-testid="demo-view-switcher">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-1.5 flex items-center">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 pl-3 pr-2.5 py-1.5 text-xs font-bold transition"
          data-testid="demo-switcher-toggle"
        >
          <Sparkles size={13} className="text-hv-gold" />
          Demo mode · Viewing as {current.label}
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div className="absolute top-full left-5 sm:left-8 mt-1 w-56 rounded-2xl bg-white border border-[#E5DFC8] shadow-xl p-1.5 z-[60]" data-testid="demo-switcher-panel">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = v.target === user.demo_slot;
            return (
              <button
                key={v.target}
                type="button"
                onClick={() => switchTo(v.target)}
                disabled={switching}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold text-left transition ${
                  active ? "bg-hv-tealTint text-hv-navy" : "text-hv-navy hover:bg-hv-cream"
                }`}
                data-testid={`demo-switch-${v.target}`}
              >
                <Icon size={16} strokeWidth={2.5} className={active ? "text-hv-teal" : "text-hv-slate"} />
                {v.label}
                {active && <span className="ms-auto text-[10px] uppercase tracking-wider text-hv-teal">Now</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
